<template>
  <ActionButton mode="secondary" :actions="actions" :loading="loading" />
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import { useFlag } from '@unleash/proxy-client-vue';
import { computed, PropType, Ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { RygenMenuItem } from '@rygen/rygen-ui/model';

import {
  CargoInsuranceStatus,
  LoadboardSource,
  Order,
  OrderStatus,
  RateShopAction,
  Shipment,
  ShipmentStatus,
} from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useLoadboardIntegrationStore } from '@/stores/LoadboardIntegration';
import { useParentEntityStore } from '@/stores/ParentEntity';

import ActionButton from '@rygen/rygen-ui/components/button/ActionButton.vue';

const props = defineProps({
  cargoInsuranceStatus: {
    type: String as PropType<CargoInsuranceStatus | undefined>,
    default: () => undefined,
  },
  spotQuotable: {
    type: Boolean,
    default: () => false,
  },
  loadboardable: {
    type: Boolean,
    default: () => false,
  },
  updatable: {
    type: Boolean,
    default: () => false,
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
  brokerageEnabled: {
    type: Boolean,
    default: () => false,
  },
  waterfallTenderEnabled: {
    type: Boolean,
    default: () => false,
  },
  canRequestSpotMarketBid: {
    type: Boolean,
    default: () => false,
  },
  showCargoInsurance: {
    type: Boolean,
    default: () => false,
  },
  isRateSelectionReasonRequired: {
    type: Boolean,
    default: () => false,
  },
  activeLoadboards: {
    type: Array<LoadboardSource>,
    default: () => [],
  },
});
const {
  cargoInsuranceStatus,
  spotQuotable,
  loadboardable,
  updatable,
  loading,
  brokerageEnabled,
  waterfallTenderEnabled,
  canRequestSpotMarketBid,
  showCargoInsurance,
  isRateSelectionReasonRequired,
  activeLoadboards,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'add-spot-quote'): void;
  (event: 'cargo-insurance-cancel'): void;
  (event: 'cargo-insurance-purchase'): void;
  (event: 'remove-loadboard', loadboard: LoadboardSource): void;
  (event: 'request-spot-market-bid'): void;
  (event: 'send-to-brokerage'): void;
  (event: 'update-dat-benchmark'): void;
  (event: 'update-rates'): void;
  (event: 'waterfall-tender'): void;
  (event: 'post-to-loadboard'): void;
}>();

const isWaterfallTenderingEnabled = useFlag('waterfallTendering');
const isLoadboardEnabled = useFlag('loadboard-feat-flag');
const isDatEnabled = useFlag('loadboard-dat-feat-flag');
const isTruckstopEnabled = useFlag('loadboard-truckstop-feat-flag');
const { t } = useI18n();
const { selectedDomainId } = useCurrentUserStore();
const { owningClient, parentEntity } = useParentEntityStore();
const { hasConfiguredSource, loadConfiguredLoadboards } =
  useLoadboardIntegrationStore();

const isCanceled = computed(() => {
  if (parentEntity.value instanceof Shipment) {
    return parentEntity?.value?.status === ShipmentStatus.CANCELED;
  } else if (parentEntity.value instanceof Order) {
    return parentEntity?.value?.status === OrderStatus.CANCELED;
  } else {
    return false;
  }
});

const validLoadboardSources = computed(() =>
  Object.values(LoadboardSource)
    .filter((s) => isDatEnabled.value || LoadboardSource.DAT !== s)
    .filter((s) => isTruckstopEnabled.value || LoadboardSource.TRUCKSTOP !== s)
    .filter(hasConfiguredSource),
);

const shouldShowPostToLoadboardOption = computed(() =>
  validLoadboardSources.value.some((s) => !activeLoadboards.value.includes(s)),
);

const actions: Ref<RygenMenuItem[]> = computed(() => [
  {
    key: RateShopAction.SPOT_QUOTE,
    label: t('common.addParam', { param: t('rateShop.quotes') }),
    icon: PrimeIcons.PLUS,
    command: () => emit('add-spot-quote'),
    requiredAuthorization: ['spotQuote', 'create'],
    visible: spotQuotable.value,
  },
  {
    key: RateShopAction.SEND_TO_BROKERAGE,
    label: t('brokerages.sendToBrokerage'),
    icon: PrimeIcons.SEND,
    command: () => emit('send-to-brokerage'),
    requiredAuthorization: ['rateShop', 'broker'],
    visible: spotQuotable.value && brokerageEnabled.value,
  },
  {
    key: RateShopAction.WATERFALL_TENDER,
    label: t('waterfallTender.waterfallTender'),
    icon: PrimeIcons.LIST,
    command: () => emit('waterfall-tender'),
    requiredAuthorization: ['shipment', 'update'],
    visible:
      waterfallTenderEnabled.value &&
      isWaterfallTenderingEnabled.value &&
      !isRateSelectionReasonRequired.value,
  },
  {
    key: RateShopAction.RERATE,
    label: t('rateShop.updateRates'),
    icon: PrimeIcons.REFRESH,
    command: () => emit('update-rates'),
    requiredAuthorization: ['rateShop', 'reRate'],
    visible: updatable.value,
  },
  {
    key: RateShopAction.REQUEST_SPOT_MARKET_BID,
    label: t('carrierPortal.spotMarket.requestBid'),
    icon: PrimeIcons.DOLLAR,
    command: () => emit('request-spot-market-bid'),
    visible: canRequestSpotMarketBid.value,
  },
  {
    key: RateShopAction.CARGO_INSURANCE_PURCHASE,
    label: t('shipments.insurance.getInsuranceQuote'),
    icon: 'pi pi-money-bill',
    command: () => emit('cargo-insurance-purchase'),
    requiredAuthorization: ['shipment', 'purchaseCargoInsurance'],
    visible:
      cargoInsuranceStatus?.value !== 'CERTIFICATE_ISSUED' &&
      showCargoInsurance.value &&
      !isCanceled.value,
  },
  {
    key: RateShopAction.CARGO_INSURANCE_CANCEL,
    label: t('cargoInsurance.actions.cancelPolicy'),
    icon: 'pi pi-money-bill',
    command: () => emit('cargo-insurance-cancel'),
    requiredAuthorization: ['shipment', 'purchaseCargoInsurance'],
    visible:
      cargoInsuranceStatus?.value === 'CERTIFICATE_ISSUED' &&
      showCargoInsurance.value,
  },
  {
    key: RateShopAction.REMOVE_FROM_DAT,
    label: t('rateShop.removeFromDat'),
    icon: 'pi pi-times-circle',
    command: () => emit('remove-loadboard', LoadboardSource.DAT),
    visible:
      loadboardable.value &&
      isLoadboardEnabled.value &&
      activeLoadboards.value.includes(LoadboardSource.DAT),
  },
  {
    key: RateShopAction.REMOVE_FROM_TRUCKSTOP,
    label: t('rateShop.removeFromTruckstop'),
    icon: 'pi pi-times-circle',
    command: () => emit('remove-loadboard', LoadboardSource.TRUCKSTOP),
    visible:
      loadboardable.value &&
      isLoadboardEnabled.value &&
      activeLoadboards.value.includes(LoadboardSource.TRUCKSTOP),
  },
  {
    key: RateShopAction.POST_TO_LOADBOARD,
    label: t('rateShop.postToLoadboard'),
    icon: PrimeIcons.TABLE,
    command: () => emit('post-to-loadboard'),
    visible:
      loadboardable.value &&
      isLoadboardEnabled.value &&
      shouldShowPostToLoadboardOption.value,
  },
]);

watch([selectedDomainId, owningClient], () => loadConfiguredLoadboards(), {
  immediate: true,
});
</script>
